import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import ZohoLogo from '../../../components/ZohoLogo';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import EditContactModal from './EditContactModal';
import StatusBadge from '../../candidates-list/components/StatusBadge';
import PrioritizeButton from '../../candidates-list/components/PrioritizeButton';
import SyncToAtsButton from '../../candidates-list/components/SyncToAtsButton';
import Tooltip from '../../../components/ui/Tooltip';
import { useProfile } from '../../../hooks';
import { planHasAtsIntegration, planCanPrioritize } from '../../../lib/utils/planEntitlements';
import { cn } from '../../../utils/cn';

const CAREER_STATUS_KEYS = {
  'Open to Work': 'candidateProfile.careerOpenToWork',
  'Potential Open to Work': 'candidateProfile.careerPotentialOpenToWork',
  Pending: 'candidateProfile.careerPending',
  'Not Open to Work': 'candidateProfile.careerNotOpenToWork',
};

const AiAssessment = ({ text }) => {
  const { t } = useTranslation();
  const clampMeasureRef = useRef(null);
  const measureRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const clamped = clampMeasureRef.current;
    const measure = measureRef.current;
    if (!clamped || !measure) {
      setIsTruncated(false);
      return undefined;
    }

    const updateTruncation = () => {
      setIsTruncated(measure.offsetHeight > clamped.offsetHeight + 1);
    };

    updateTruncation();

    const frameId = window.requestAnimationFrame(updateTruncation);
    const observer = new ResizeObserver(updateTruncation);
    observer.observe(clamped);
    observer.observe(measure);

    return () => {
      window.cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [text]);

  return (
    <div className="mt-2 max-w-[240px] text-left">
      <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
        {t('candidateProfile.aiAssessment')}
      </h3>

      <div className="relative">
        <p
          ref={measureRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 invisible h-auto w-full text-xs md:text-sm leading-relaxed"
        >
          {text}
        </p>
        <p
          ref={clampMeasureRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 invisible h-auto w-full text-xs md:text-sm leading-relaxed line-clamp-3"
        >
          {text}
        </p>
        <p
          className={cn(
            'text-xs md:text-sm text-muted-foreground leading-relaxed',
            !isExpanded && 'line-clamp-3'
          )}
        >
          {text}
        </p>
      </div>

      {isTruncated && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="text-xs md:text-sm text-primary font-medium mt-1 hover:underline"
        >
          {isExpanded ? t('common.showLess') : t('common.readMore')}
        </button>
      )}
    </div>
  );
};

const ProfileHeader = ({
  candidate,
  onDeleteCandidate,
  onUpdateContact,
  onSyncToATS,
  syncing = false,
  onTogglePriority,
  onSetOpenToWorkManual,
  onReturnAiWatch,
  isPrioritized = false,
  hasPhone = false,
  showDelete = true,
  showDemoStatusNotice = false,
  showAiAssessmentWhenPresent = false,
  hideCareerStatus = false,
  unavailableInfo = '',
}) => {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingOpenStatus, setPendingOpenStatus] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  const handleConfirmStatusChange = async () => {
    if (pendingOpenStatus == null) return;
    setStatusSubmitting(true);
    try {
      await onSetOpenToWorkManual?.(pendingOpenStatus);
      setPendingOpenStatus(null);
    } catch (err) {
      console.error('Failed to set open-to-work status:', err);
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleReturnAiWatchClick = async () => {
    setStatusSubmitting(true);
    try {
      await onReturnAiWatch?.();
    } catch (err) {
      console.error('Failed to return AI watch:', err);
    } finally {
      setStatusSubmitting(false);
    }
  };

  const plan = (profile?.subscription_plan ?? '').toLowerCase();
  const canPrioritize = planCanPrioritize(plan);
  const showSyncSlot = planHasAtsIntegration(plan);
  const canSync = Boolean(candidate?.isFromZoho && candidate?.hasScraped);

  // Generate initials from full name
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const displayField = (value) => (
    !value?.trim() || value === 'Not specified' ? t('common.notSpecified') : value
  );

  const careerStatusLabel = CAREER_STATUS_KEYS[candidate?.careerStatus]
    ? t(CAREER_STATUS_KEYS[candidate.careerStatus])
    : candidate?.careerStatus;

  return (
    <div className="relative bg-card rounded-lg border border-border p-4 md:p-6 lg:p-8 shadow-elevation-sm">
      {candidate?.isFromZoho && (
        <div className="absolute top-3 left-3 md:top-4 md:left-4 z-10">
          <Tooltip content={t('candidates.fromZoho')} align="start" size="sm">
            <span className="inline-flex items-center justify-center">
              <ZohoLogo size={22} />
            </span>
          </Tooltip>
        </div>
      )}
      <div className="flex flex-col lg:flex-row gap-4 md:gap-6 lg:gap-8">
        {/* Avatar Section */}
        <div className="flex flex-col items-center lg:items-start flex-shrink-0 gap-4">
          <div className="relative">
            {candidate?.avatar ? (
              <Image
                src={candidate?.avatar}
                alt={candidate?.avatarAlt}
                className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 rounded-full object-cover border-4 border-border"
              />
            ) : (
              <div className="w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-3xl md:text-4xl lg:text-5xl font-semibold border-4 border-border">
                {getInitials(candidate?.name)}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 w-32 md:w-40 lg:w-48">
            {canPrioritize && (
              <PrioritizeButton
                isPrioritized={isPrioritized}
                hasPhone={hasPhone}
                onClick={onTogglePriority}
                size="sm"
                fullWidth
              />
            )}

            {showSyncSlot && (
              <div
                className={!canSync ? 'invisible pointer-events-none' : undefined}
                aria-hidden={!canSync}
              >
                <SyncToAtsButton
                  onClick={onSyncToATS}
                  loading={syncing}
                  size="sm"
                  fullWidth
                />
              </div>
            )}

            {showDelete && (
              <Button
                variant="destructive"
                size="sm"
                fullWidth
                onClick={() => setShowDeleteConfirm(true)}
                iconName="Trash2"
                className="mt-3"
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="flex-1 flex flex-col lg:flex-row gap-4 md:gap-6 lg:gap-8">
          <div className="flex-1 text-center lg:text-left">
          {candidate?.isInvalidLink && (
            <div className="flex items-center justify-center lg:justify-start gap-2.5 mb-3">
              <StatusBadge status="unavailable" size="md" />
              <Tooltip content={unavailableInfo || t('candidateProfile.unavailableInfo')} align="start" size="md">
                <button
                  type="button"
                  className="text-red-600 cursor-help inline-flex"
                  aria-label={t('candidateProfile.unavailableAria')}
                >
                  <Icon name="Info" size={20} />
                </button>
              </Tooltip>
            </div>
          )}

          <div className="flex items-center justify-center lg:justify-start gap-2 mb-2 md:mb-3 flex-wrap">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
              {candidate?.name}
            </h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowEditModal(true)}
              iconName="Pencil"
              aria-label={t('candidateProfile.editContactAria')}
              className="text-muted-foreground hover:text-white hover:bg-primary flex-shrink-0"
            />
          </div>
          
          <div className="flex flex-col gap-2 md:gap-3 text-sm md:text-base text-muted-foreground">
            <div className="flex items-center justify-center lg:justify-start gap-2">
              <Icon name="MapPin" size={18} className="flex-shrink-0" />
              <span>{displayField(candidate?.location)}</span>
            </div>
            
            <div className="flex items-center justify-center lg:justify-start gap-2">
              <Icon name="Briefcase" size={18} className="flex-shrink-0" />
              <span>{displayField(candidate?.currentPosition)}</span>
            </div>
            
            <div className="flex items-center justify-center lg:justify-start gap-2">
              <Icon name="Building2" size={18} className="flex-shrink-0" />
              <span>{displayField(candidate?.company)}</span>
            </div>

            <div className="flex items-center justify-center lg:justify-start gap-2">
              <Icon name="Phone" size={18} className="flex-shrink-0" />
              {candidate?.phone?.trim() ? (
                <a
                  href={`tel:${candidate.phone}`}
                  className="text-primary hover:underline truncate max-w-full font-data"
                >
                  {candidate.phone}
                </a>
              ) : (
                <span>{t('common.notSpecified')}</span>
              )}
            </div>

            <div className="flex items-center justify-center lg:justify-start gap-2">
              <Icon name="Mail" size={18} className="flex-shrink-0" />
              {candidate?.email?.trim() ? (
                <a
                  href={`mailto:${candidate.email}`}
                  className="text-primary hover:underline truncate max-w-full"
                >
                  {candidate.email}
                </a>
              ) : (
                <span>{t('common.notSpecified')}</span>
              )}
            </div>

            {candidate?.linkedinUrl && (
              <div className="flex items-center justify-center lg:justify-start gap-2">
                <Icon name="Linkedin" size={18} className="flex-shrink-0" />
                <a
                  href={candidate.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline truncate max-w-full"
                >
                  {candidate.linkedinUrl}
                </a>
              </div>
            )}
          </div>

          </div>

          {!hideCareerStatus && (
          <div className="flex flex-col items-center lg:items-end justify-start text-center lg:text-right flex-shrink-0 lg:pt-1">
            <div
              className="flex items-center justify-center lg:justify-end gap-2 text-sm md:text-base text-muted-foreground"
              title={t('candidateProfile.lastChecked')}
              aria-label={t('candidateProfile.lastCheckedAria', { date: candidate?.lastLinkedInCheck })}
            >
              <Icon name="Clock" size={18} className="flex-shrink-0" />
              <span className="font-semibold">{candidate?.lastLinkedInCheck}</span>
            </div>

            <div className="flex items-center justify-center lg:justify-end gap-2 mt-1 mb-2">
              <Icon name="TrendingUp" size={20} className="text-primary" />
              <h3 className="text-base md:text-lg font-medium text-foreground">{t('candidateProfile.careerStatus')}</h3>
            </div>

            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm md:text-base font-medium ${
              candidate?.careerStatus === 'Open to Work'
                ? 'bg-success/10 text-success border border-success/20'
                : candidate?.careerStatus === 'Potential Open to Work'
                  ? 'bg-lime-100 text-lime-900 border border-lime-200'
                  : candidate?.careerStatus === 'Pending'
                    ? 'bg-amber-100 text-amber-800 border border-amber-200'
                    : 'bg-muted text-muted-foreground border border-border'
            }`}>
              <Icon
                name={
                  candidate?.careerStatus === 'Open to Work'
                    ? 'CheckCircle2'
                    : candidate?.careerStatus === 'Potential Open to Work'
                      ? 'TrendingUp'
                      : candidate?.careerStatus === 'Pending'
                        ? 'Clock'
                        : 'MinusCircle'
                }
                size={18}
              />
              {careerStatusLabel}
            </span>

            {showDemoStatusNotice && (
              <p className="mt-2 max-w-[240px] text-xs text-muted-foreground text-center lg:text-right leading-relaxed">
                {t('demo.statusMayUpdateNotice')}
              </p>
            )}

            {(candidate?.careerStatus === 'Potential Open to Work' || showAiAssessmentWhenPresent)
              && candidate?.reasoningOpenToWork && (
              <AiAssessment text={candidate.reasoningOpenToWork} />
            )}

            {candidate?.careerStatus === 'Potential Open to Work' && !candidate?.isManualOpenToWork && (
              <div className="mt-3 flex flex-col gap-2 w-full max-w-[240px]">
                <p className="text-xs text-muted-foreground text-left">{t('candidateProfile.setStatusManually')}</p>
                <div className="flex flex-col gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="CheckCircle2"
                    iconPosition="left"
                    onClick={() => setPendingOpenStatus(true)}
                  >
                    {t('candidateProfile.openToWork')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="MinusCircle"
                    iconPosition="left"
                    onClick={() => setPendingOpenStatus(false)}
                  >
                    {t('candidateProfile.notOpenToWork')}
                  </Button>
                </div>
              </div>
            )}

            {candidate?.isManualOpenToWork && (
              <div className="mt-3 flex flex-col gap-2 w-full max-w-[240px]">
                <p className="text-xs text-muted-foreground text-left">
                  {t('candidateProfile.statusSetManually', {
                    onDate: candidate?.manualOpenToWorkAt
                      ? t('candidateProfile.statusSetManuallyOn', { date: candidate.manualOpenToWorkAt })
                      : '',
                  })}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  loading={statusSubmitting}
                  onClick={handleReturnAiWatchClick}
                  className="border-destructive/50 text-destructive hover:!bg-destructive hover:!text-destructive-foreground hover:!border-destructive"
                >
                  {t('candidateProfile.returnAiWatch')}
                </Button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
      {createPortal(
        <EditContactModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          candidate={candidate}
          onSave={onUpdateContact}
        />,
        document.body
      )}
      {createPortal(
        <ConfirmDialog
          isOpen={pendingOpenStatus != null}
          onClose={() => setPendingOpenStatus(null)}
          onConfirm={handleConfirmStatusChange}
          title={pendingOpenStatus ? t('candidateProfile.markOpenToWork') : t('candidateProfile.markNotOpenToWork')}
          message={t('candidateProfile.manualStatusMessage')}
          confirmText={statusSubmitting ? t('candidateProfile.saving') : t('common.confirm')}
          cancelText={t('common.cancel')}
          variant="default"
        />,
        document.body
      )}
      {showDelete && createPortal(
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            onDeleteCandidate?.();
            setShowDeleteConfirm(false);
          }}
          title={t('candidates.deleteCandidateTitle')}
          message={t('candidates.deleteCandidateMessage', { name: candidate?.name })}
          confirmText={t('common.delete')}
          cancelText={t('common.cancel')}
        />,
        document.body
      )}
    </div>
  );
};

export default ProfileHeader;
